import * as Sentry from "@sentry/react";
import { t } from "i18next";
import appConfig from './app.config';

const env = window?.webui?.env || process.env.WEBUI_ENV || 'development';

export default {
  env,

  sentry: {
    dsn: "https://2bdba2594bf03a648a4d93af26a146d0@o4507406655356928.ingest.de.sentry.io/4507406663483472",

    integrations: [
      Sentry.browserTracingIntegration({
        // disable automatic span creation
        instrumentNavigation: false,
        instrumentPageLoad: false,
      }),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: "light",
        showName: true,
        isNameRequired: true,
        showEmail: false,
        showBranding: false,
        useSentryUser: {
        email: "username",
        },
      }),
    ],

    environment: env,
    enabled: env !== 'development',

    //
    // Performance Monitoring
    //
    // Capture 100% of the transactions
    tracesSampleRate: 1.0,
    // Set 'tracePropagationTargets' to control for which URLs distributed
    // tracing should be enabled
    tracePropagationTargets: ["localhost", /^https?:\/\/tonique\.com/, "player-dev.tonique.com:8080"],
    // Session Replay
    // This sets the sample rate at 10%. You may want to change it to 100%
    // while in development and then sample at a lower rate in production.
    replaysSessionSampleRate: 0.1,
    // If you're not already sampling the entire session,
    // change the sample rate to 100% when sampling sessions where errors occur.
    replaysOnErrorSampleRate: 1.0
  },

  apiBaseUrl: {
    development: 'https://ha-dev.tonique.com',
    staging: 'https://ha-test.tonique.com',
    production: 'https://ha.tonique.com',
  }[window?.webui?.env || process.env.WEBUI_ENV || 'development'],

  cookieDomain: {
    development: '.tonique.com',
    staging: '.tonique.com',
    production: '.tonique.com',
  }[window?.webui?.env || process.env.WEBUI_ENV || 'development'],

  cubeImageLandscapeUrl : 'https://res.cloudinary.com/dmy7iqq9b/image/upload/t_smart_crop_fill/t_cube_land/:CubeImageId.jpg',
  cubeImagePortraitUrl : 'https://res.cloudinary.com/dmy7iqq9b/image/upload/t_smart_crop_fill/t_cube_portrait/:CubeImageId.jpg',

  /**
   * Interval we ping the TMS to signalize the online/offline state
   */
  getStatusInterval: 12000,

  /**
   * Interval we check for new deployed app versions (in ms)
   */
  versionCheckInterval: 60000,

  /**
   * Persist song blobs in local storage
   * (needs increase of local storage memory in browser settings!)
   *
   * @todo not finished yet
   */
  persistInLocalStorage: false,

  /**
   * Show onboarding
   */
  showOnboarding: true,

  /**
   * Number of preceding and following songs to show in history
   * in the station view
   */
  stationViewHistoryLimits: [10, 3],

  /**
   * Show precedings songs on its respective player types
   */
  stationViewShowPrecedingSongs: {
    Cube: true,
    Web: true
  },

  /**
   * Max. seconds we keep station events in local storage
   */
  stationEventsMaxAgeInSeconds: 24 * 60 * 60 * 3,

  /**
   * Allowed leeway in seconds we allow to consider a jingle
   * stopped on its own (i.e. TMSCommunication-delay plus a bit more)
   */
  remoteJingleStopLeeway: 30.0,

  /**
   * Steps for "Getting started tour"
   */
    tourSteps: ({ WebId, cubes, stations, jingles }) => {
      let onlineCube = Object.values(cubes).find(cube => cube?.isOnline);
      if (!onlineCube) {
        onlineCube = Object.values(cubes)[0];
      }
      const steps = [
        {
          title: t('tour.intro.title'),
          intro: t('tour.intro.intro'),
          location: "/"
        },
        ...(Object.values(cubes).length > 1
          ? [
        {
          element: ".app-player-web",
          title: t('tour.app-player-web.title'),
          intro: t('tour.app-player-web.intro'),
          location: "/"
        }]: []),
        {
          element: `.app-station-${stations[0]?.id}`,
          title: t('tour.app-station-main.title'),
          intro: t('tour.app-station-main.intro'),
          location: `/${WebId}/1`
        },
        {
          element: ".app-player-web-info",
          title: t('tour.app-player-web-info.title'),
          intro: t('tour.app-player-web-info.intro'),
          location: `/${WebId}/1`
        },
        {
          element: ".app-player-web-history",
          title: t('tour.app-player-web-history.title'),
          intro: t('tour.app-player-web-history.intro'),
          location: `/${WebId}/1/${stations[0]?.id}`
        },
        ...(Object.values(cubes).length > 1
          ? [
        {
          element: ".app-navigate-playeroverview",
          title: t('tour.app-navigate-playeroverview.title'),
          intro: t('tour.app-navigate-playeroverview.intro'),
          location: `/${WebId}/1/${stations[0]?.id}`
        },
        {
          element: ".app-player-toggle",
          title: t('tour.app-player-toggle.title'),
          intro: t('tour.app-player-toggle.intro'),
          location: "/"
        },
        {
          element: `.app-player-${onlineCube?.id}`,
          title: t('tour.app-player.title'),
          intro: t('tour.app-player.intro'),
          location: "/"
        },
        {
          element: `.app-player-channel-${onlineCube?.channels[1]?.channel}`,
          title: t('tour.app-player-channel.title'),
          intro: t('tour.app-player-channel.intro'),
          location: `/${onlineCube?.id}/1`
        },
        {
          element: `.app-station-${stations[0]?.id}`,
          title: t('tour.app-station.title'),
          intro: t('tour.app-station.intro'),
          location: `${onlineCube?.id}/1`
        },
        {
          element: ".app-player-web-info",
          title: t('tour.app-player-web-station-info.title'),
          intro: t('tour.app-player-web-station-info.intro'),
          location: `${onlineCube?.id}/1`
        },
        {
          element: ".app-volume-bar",
          title: t('tour.app-volume-bar.title'),
          intro: t('tour.app-volume-bar.intro'),
          location: `${onlineCube?.id}/1`,
          position: "left"
        }]: [
          {
          element: ".app-volume-bar",
          title: t('tour.app-volume-bar.title'),
          intro: t('tour.app-volume-bar.intro'),
          location: "/",
          position: "left"
        }
        ]),
        {
          title: t('tour.ready-to-start.title'),
          intro: t('tour.ready-to-start.body'),
          location: "/"
        }
      ];

    if (appConfig?.sentry?.enabled) {
      steps.splice(steps.length - 1, 0, {
        element: ".app-report-issue-button",
        title: t('tour.app-report-issue.title'),
        intro: t('tour.app-report-issue.intro'),
        location: `${cubes[0]?.id}/1`,
        position: "left"
      });
    }
    return steps;
  }
};
